/**
 * BRRRR solution page — Buy, Rehab, Rent, Refinance, Repeat.
 */
import { Repeat, Hammer, Home, Landmark, TrendingUp, Brain } from "lucide-react";
import SolutionPageTemplate from "../SolutionPageTemplate";

export default function BrrrrSolutionPage() {
  return (
    <SolutionPageTemplate
      slug="brrrr"
      persona="operators building portfolios with recycled capital"
      eyebrow="Solutions · BRRRR"
      title="Buy. Rehab. Rent."
      titleAccent="Refinance. Repeat."
      subtitle="BRRRR only works when the refinance appraisal matches the underwriting assumption. iSpeedToClose scores each step against live comps and rent data — so the refi actually returns your capital."
      bullets={[
        "Pre-purchase BRRRR-fit score per ZIP (BP-024) and per property (BP-014)",
        "Rehab evidence with contractor draws captured on the timeline",
        "Post-stabilization refi window automatically flagged (BP-023)",
      ]}
      metricStrip={[
        { stat: "76%", label: "avg. capital returned per refi" },
        { stat: "5", label: "steps, one workspace" },
        { stat: "9.2%", label: "avg. stabilised yield" },
        { stat: "3.1", label: "avg. cycles per year" },
      ]}
      pains={[
        { title: "Refi appraisal falls short", desc: "You underwrote to $210k ARV. Appraiser comes in at $178k. Your capital is stuck." },
        { title: "Rent doesn't stabilise", desc: "Optimistic rent projection. Real vacancy. DSCR doesn't clear lender minimum." },
        { title: "Rehab creep", desc: "Repairs balloon. Timeline slips. The refi window slides into a rising rate environment." },
        { title: "Losing track of cycles", desc: "Which property is at which stage across five active BRRRRs? Spreadsheet chaos." },
      ]}
      playbook={[
        { step: 1, title: "Buy", desc: "BRRRR-fit score + ARV comps + repair estimate + strategy compare.", bp: "BP-024 · BP-004 · BP-003 · BP-014" },
        { step: 2, title: "Rehab", desc: "Contractor draws, photos, timeline — captured immutably.", bp: "BP-002" },
        { step: 3, title: "Rent", desc: "Stabilization tracker + rent-comp evidence + DSCR check.", bp: "BP-021" },
        { step: 4, title: "Refi & repeat", desc: "Refi window fires when equity + DSCR align. Capital recycled.", bp: "BP-023" },
      ]}
      productsUsed={[
        { icon: Repeat, title: "Portfolio Intelligence", to: "/products/portfolio-intelligence", desc: "BRRRR radar flags refi-ready properties across your entire portfolio, nightly." },
        { icon: Landmark, title: "Strategy Comparison", to: "/products/strategy-comparison", desc: "BRRRR scored against Flip and Buy-and-Hold on every property, side-by-side." },
        { icon: Hammer, title: "Property Workspace", to: "/products/property-workspace", desc: "Every step of the 5-letter cycle lives inside one property workspace." },
        { icon: TrendingUp, title: "Market Intelligence", to: "/products/market-intelligence", desc: "BRRRR fit ranked per ZIP — buy where rent yield + appreciation compound." },
        { icon: Brain, title: "AI Acquisition Coach", to: "/products/ai-acquisition-coach", desc: "Confidence-capped BRRRR recommendation with rent + refi evidence cited." },
        { icon: Home, title: "Executive Dashboard", to: "/products/executive-dashboard", desc: "Capital deployed, capital returned, cycles-per-year — one view." },
      ]}
      proof={{
        stat: "3.1",
        label: "BRRRR cycles per year",
        quote: "We went from 1 BRRRR per year on gut feel to 3 per year on evidence. The refi radar alone was worth the subscription.",
        attribution: "Portfolio operator · 14-door BRRRR fund",
      }}
      related={[
        { title: "Buy & Hold", to: "/solutions/buy-and-hold", desc: "Stabilized rentals — the second half of every BRRRR." },
        { title: "Fix & Flip", to: "/solutions/fix-and-flip", desc: "Same rehab engine when the exit is a sale, not a refi." },
        { title: "Creative Finance", to: "/solutions/creative-finance", desc: "Sub-To acquisitions that flow into BRRRR cycles." },
      ]}
    />
  );
}
